/*
 * FIND YOUR FATE - Product Recommendations v2.1
 * Dice Dungeons | Rules-based, class-collection-only matching
 * Requires: templates/collection.fyf-colors.liquid
 */
(function () {
  'use strict';

  // Hard cap for suggested dice sets. Browsing the full class collection has no cap.
  const MAX_DICE_PRICE = 100;

  const classCollections = {
    Barbarian: 'class-barbarian', Bard: 'class-bard',
    Cleric: 'class-cleric', Druid: 'class-druid',
    Fighter: 'class-fighter', Monk: 'class-monk',
    Paladin: 'class-paladin', Ranger: 'class-ranger',
    Rogue: 'class-rogue', Sorcerer: 'class-sorcerer',
    Warlock: 'class-warlock', Wizard: 'class-wizard'
  };

  // First entries are the strongest color preferences.
  const classColors = {
    Barbarian: ['Red', 'Bronze', 'Black', 'Brown'],
    Bard: ['Purple', 'Pink', 'Gold', 'Multicolor'],
    Cleric: ['White', 'Gold', 'Silver', 'Yellow'],
    Druid: ['Green', 'Brown', 'Teal', 'Bronze'],
    Fighter: ['Silver', 'Gray', 'Bronze', 'Black'],
    Monk: ['White', 'Gray', 'Brown', 'Blue'],
    Paladin: ['Gold', 'Silver', 'White', 'Blue'],
    Ranger: ['Green', 'Brown', 'Black', 'Teal'],
    Rogue: ['Black', 'Gray', 'Purple', 'Navy'],
    Sorcerer: ['Purple', 'Red', 'Blue', 'Multicolor'],
    Warlock: ['Purple', 'Black', 'Red', 'Silver'],
    Wizard: ['Blue', 'Purple', 'Silver', 'Teal']
  };

  const bookPreferences = {
    classic: {
      colors: ['Gold', 'Blue', 'Green', 'Bronze'],
      tags: ['Ancient', 'Royal', 'DragonScale', 'Gemstone']
    },
    mutated: {
      colors: ['Green', 'Purple', 'Teal', 'Black'],
      tags: ['Glow-In-The-Dark', 'Void Relic', 'StarGlow', 'Sharp', 'Resin']
    },
    eighties: {
      colors: ['Pink', 'Teal', 'Multicolor', 'Purple', 'Yellow', 'Orange'],
      tags: ['Groovy Stone', 'Speakeasy', 'Sparkle', 'Swirl', 'Tri-Color']
    },
    cryptid: {
      colors: ['Green', 'Brown', 'Gray', 'Black', 'Navy'],
      tags: ['Ancient', 'Wood', 'Stone', 'Gemstone', 'Sea Glass']
    }
  };

  const classTags = {
    Barbarian: ['metal', 'Cage', 'DragonScale'],
    Bard: ['Speakeasy', 'Sparkle', 'Swirl'],
    Cleric: ['Royal', 'Gemstone', 'StarGlow'],
    Druid: ['Wood', 'Stone', 'Gemstone'],
    Fighter: ['metal', 'Cage', 'Fortress Dice'],
    Monk: ['Stone', 'Wood', 'Hollow'],
    Paladin: ['Royal', 'metal', 'Fortress Dice'],
    Ranger: ['Wood', 'Stone', 'Gemstone'],
    Rogue: ['Hollow', 'Void Relic', 'Sharp'],
    Sorcerer: ['StarGlow', 'Sparkle', 'Swirl'],
    Warlock: ['Void Relic', 'Ancient', 'Glow-In-The-Dark'],
    Wizard: ['StarGlow', 'Gemstone', 'Ancient']
  };

  const knownColors = [
    'silver', 'gold', 'black', 'blue', 'gray', 'brown', 'bronze',
    'red', 'purple', 'white', 'teal', 'green', 'clear', 'pink',
    'yellow', 'multicolor', 'beige', 'rainbow', 'orange', 'navy'
  ];
  const ignoredColorValues = ['dots', 'striped', 'animal', 'hunter'];
  const colorAliases = { grey: 'gray', gray: 'gray', multi: 'multicolor',
    multicolour: 'multicolor', multicolor: 'multicolor',
    metallic: '', transparent: 'clear' };

  // A small story signal, used only as a tie-breaker.
  const storyColors = [
    { test: /forest|wood|marsh|root|wilderness|animal|grove|creature/i,
      colors: ['Green', 'Brown'] },
    { test: /royal|noble|crown|honor|oath|kingdom/i,
      colors: ['Gold', 'Silver'] },
    { test: /secret|shadow|night|tomb|forbidden|disappear/i,
      colors: ['Black', 'Purple'] },
    { test: /flame|fire|battle|rage|dragon/i,
      colors: ['Red', 'Bronze'] },
    { test: /storm|river|lake|sea|coast/i,
      colors: ['Blue', 'Teal'] }
  ];

  const collectionCache = new Map();
  const colorCache = new Map();
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD'
  });
  let currentRequest = 0;

  function rootPath() {
    return (window.Shopify && window.Shopify.routes &&
      window.Shopify.routes.root) || '/';
  }

  function normalized(value) {
    const text = String(value == null ? '' : value).trim().toLowerCase();
    return colorAliases[text] !== undefined ? colorAliases[text] : text;
  }

  function tagsOf(product) {
    const tags = product.tags;
    if (Array.isArray(tags)) return tags;
    if (typeof tags === 'string') return tags.split(',');
    return [];
  }

  function hasTag(product, tag) {
    const target = normalized(tag);
    return tagsOf(product).some(value => normalized(value) === target);
  }

  function availableVariants(product) {
    return (product.variants || []).filter(v => v.available === true);
  }

  function isAvailable(product) {
    return availableVariants(product).length > 0;
  }

  // Check individual variants, not just the product's advertised minimum price.
  // This ensures that a $35 / $790 multi-variant product only offers the
  // affordable variant on its recommendation card.
  function affordableDiceVariants(product) {
    return availableVariants(product).filter(variant => {
      const price = Number(variant.price);
      return Number.isFinite(price) && price >= 0 && price <= MAX_DICE_PRICE;
    });
  }

  function isDiceSet(product) {
    if (['Dice Tray', 'Dice Bag', 'Dice Box'].some(tag => hasTag(product, tag))) {
      return false;
    }
    return ['full set', 'dice', 'Class Dice'].some(tag => hasTag(product, tag));
  }

  function lowestPrice(product) {
    const prices = affordableDiceVariants(product).map(v => Number(v.price));
    return prices.length ? Math.min(...prices) : Infinity;
  }

  function memoized(cache, key, loader) {
    if (cache.has(key)) return cache.get(key);
    const promise = loader().catch(error => {
      cache.delete(key);
      throw error;
    });
    cache.set(key, promise);
    return promise;
  }

  async function fetchCollection(handle) {
    return memoized(collectionCache, handle, async () => {
      const products = [];
      for (let page = 1; page <= 5; page++) {
        const path = rootPath() + 'collections/' + encodeURIComponent(handle) +
          '/products.json?limit=50&page=' + page;
        const response = await fetch(path, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Could not load ' + handle);
        const data = await response.json();
        if (!Array.isArray(data.products)) throw new Error('Invalid collection data');
        products.push(...data.products);
        if (data.products.length < 50) break;
      }
      return products;
    });
  }

  // Shopify category metafields are available in Liquid, but not in products.json.
  // The alternate collection template supplies only the color labels we need.
  async function fetchColors(handle) {
    return memoized(colorCache, handle, async () => {
      const colorsByHandle = new Map();
      for (let page = 1; page <= 5; page++) {
        const url = rootPath() + 'collections/' + encodeURIComponent(handle) +
          '?view=fyf-colors&page=' + page;
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Color feed unavailable: ' + handle);
        const data = await response.json();
        if (!data || data.collection !== handle || !Array.isArray(data.products)) {
          throw new Error('Color feed template missing or invalid: ' + handle);
        }
        data.products.forEach(p => {
          if (p.handle && Array.isArray(p.colors)) {
            colorsByHandle.set(p.handle, p.colors);
          }
        });
        if (Number(data.page) >= Number(data.pages)) break;
      }
      return colorsByHandle;
    });
  }

  function titleColors(product) {
    const text = String(product.title || '') + ' ' +
      availableVariants(product).map(v => v.title || '').join(' ');
    const tokens = text.toLowerCase().match(/[a-z]+/g) || [];
    const found = new Set();
    tokens.forEach(token => {
      const value = normalized(token);
      if (knownColors.includes(value)) found.add(value);
    });
    return [...found];
  }

  function productColors(product, colorsByHandle) {
    const saved = colorsByHandle && colorsByHandle.get(product.handle);
    const fromMetafield = Array.isArray(saved)
      ? saved.map(normalized).filter(c => knownColors.includes(c)) : [];
    // Prefer explicitly maintained Shopify color data over title inference.
    return [...new Set(fromMetafield.length ? fromMetafield : titleColors(product))];
  }

  function colorsForCharacter(character) {
    const mode = bookPreferences[character.bookMode] || bookPreferences.classic;
    const c = classColors[character.className] || [];
    const story = [character.origin, character.event, character.motivation]
      .filter(Boolean).join(' ');
    const storyAdditions = storyColors.filter(entry => entry.test.test(story))
      .flatMap(entry => entry.colors);
    return { classColors: c.map(normalized),
      modeColors: mode.colors.map(normalized),
      storyColors: [...new Set(storyAdditions.map(normalized))],
      modeTags: mode.tags };
  }

  function scoreProduct(product, character, colorData) {
    const colors = productColors(product, colorData);
    const palette = colorsForCharacter(character);
    let score = 0;

    const classHits = colors.filter(c => palette.classColors.includes(c));
    const modeHits = colors.filter(c => palette.modeColors.includes(c));
    const storyHits = colors.filter(c => palette.storyColors.includes(c));

    // More than one color match is meaningful, but don't reward 4-color sets endlessly.
    if (classHits.length) {
      const bestPosition = Math.min(...classHits.map(c => palette.classColors.indexOf(c)));
      score += [25, 21, 15, 10][Math.min(bestPosition, 3)];
      if (classHits.length >= 2) score += 9;
    }
    if (modeHits.length) {
      const bestPosition = Math.min(...modeHits.map(c => palette.modeColors.indexOf(c)));
      score += [23, 19, 15, 11, 8, 6][Math.min(bestPosition, 5)];
      if (modeHits.length >= 2) score += 8;
    }
    if (storyHits.length) score += 7;

    const favoredClassTags = classTags[character.className] || [];
    score += Math.min(18, favoredClassTags.filter(tag => hasTag(product, tag)).length * 6);
    score += Math.min(20, palette.modeTags.filter(tag => hasTag(product, tag)).length * 8);
    if (hasTag(product, 'full set')) score += 10;
    if (hasTag(product, 'Exclusive')) score += 2;

    // Gentle tie-breaking only. Previously randomness could dominate weak matches.
    score += Math.random() * 1.5;
    return { product, score, colors,
      material: detectMaterial(product), price: lowestPrice(product) };
  }

  function detectMaterial(product) {
    for (const material of ['Gemstone', 'Glass', 'metal', 'Wood', 'Resin', 'polymer', 'Stone']) {
      if (hasTag(product, material)) return normalized(material);
    }
    return '';
  }

  function priceBand(price) {
    if (price < 25) return 0;
    if (price < 50) return 1;
    if (price < 75) return 2;
    return 3;
  }

  function varietyAdjustment(candidate, selected) {
    // Give meaningful alternatives room to surface without throwing away
    // the original character/class matching score.
    let adjustment = 0;
    selected.forEach(chosen => {
      if (candidate.material && chosen.material) {
        adjustment += candidate.material === chosen.material ? -22 : 6;
      }
      if (candidate.colors.length && chosen.colors.length &&
          candidate.colors.some(color => chosen.colors.includes(color))) {
        adjustment -= 12;
      }
      adjustment += priceBand(candidate.price) === priceBand(chosen.price)
        ? -12 : 5;
    });
    return adjustment;
  }

  function chooseDice(products, character, colorData) {
    const ranked = products
      .filter(isDiceSet)
      .filter(product => affordableDiceVariants(product).length > 0)
      .map(product => scoreProduct(product, character, colorData))
      .sort((a, b) => b.score - a.score);

    if (!ranked.length) return [];
    const selected = [ranked[0]];

    while (selected.length < 3) {
      const remaining = ranked.filter(item =>
        !selected.some(chosen => chosen.product.id === item.product.id));
      if (!remaining.length) break;

      // Don't sacrifice too much theme relevance just for variety.
      const strong = remaining.filter(item =>
        item.score >= ranked[0].score - 40);
      const pool = strong.length ? strong : remaining;

      const next = pool.slice().sort((a, b) =>
        (b.score + varietyAdjustment(b, selected)) -
        (a.score + varietyAdjustment(a, selected)))[0];
      selected.push(next);
    }

    return selected.map((item, index) => ({
      product: item.product,
      label: index === 0 ? 'Your Perfect Match' :
        index === 1 ? 'Another Path' :
        item.price >= selected[0].price * 1.2 &&
        item.price >= selected[0].price + 10
          ? 'Legendary Upgrade' : 'Another Worthy Set'
    }));
  }

  function chooseAccessories(products) {
    const available = products.filter(isAvailable);
    const random = items => items[Math.floor(Math.random() * items.length)];
    const trays = available.filter(p => hasTag(p, 'Dice Tray'));
    const storage = available.filter(p =>
      hasTag(p, 'Dice Bag') || hasTag(p, 'Dice Box'));
    const picks = [];
    if (trays.length) picks.push({ product: random(trays), label: 'Rolling Tray' });
    const possibleStorage = storage.filter(p =>
      !picks.some(s => s.product.id === p.id));
    if (possibleStorage.length) picks.push({
      product: random(possibleStorage), label: 'Dice Storage'
    });
    return picks;
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function productCard(product, label, isDiceCard = false) {
    const variants = (isDiceCard
      ? affordableDiceVariants(product)
      : availableVariants(product))
      .slice()
      .sort((a, b) => Number(a.price) - Number(b.price));
    if (!variants.length) return null;
    const card = element('article', 'fyf-product-card');
    card.appendChild(element('p', 'fyf-product-label', label));

    const link = element('a');
    link.href = rootPath() + 'products/' + encodeURIComponent(product.handle);
    const imageUrl = product.images && product.images[0] && product.images[0].src;
    if (imageUrl) {
      const image = element('img');
      image.src = imageUrl;
      image.alt = product.title;
      image.loading = 'lazy';
      link.appendChild(image);
    }
    link.appendChild(element('h4', '', product.title));
    card.appendChild(link);

    let chosenVariant = variants[0];
    const price = element('p', 'fyf-product-price',
      currencyFormatter.format(Number(chosenVariant.price)));
    card.appendChild(price);

    if (variants.length > 1) {
      const select = element('select');
      select.setAttribute('aria-label', 'Choose an option for ' + product.title);
      variants.forEach(v => {
        const option = element('option', '', v.title + ' - ' +
          currencyFormatter.format(Number(v.price)));
        option.value = String(v.id);
        select.appendChild(option);
      });
      select.addEventListener('change', () => {
        chosenVariant = variants.find(v => String(v.id) === select.value) || variants[0];
        price.textContent = currencyFormatter.format(Number(chosenVariant.price));
      });
      card.appendChild(select);
    }

    const button = element('button', 'fyf-add-cart', 'Add to Cart');
    button.type = 'button';
    button.addEventListener('click', async () => {
      button.disabled = true;
      button.textContent = 'Adding...';
      try {
        const response = await fetch(rootPath() + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: chosenVariant.id, quantity: 1 }] })
        });
        if (!response.ok) throw new Error('Cart could not be updated');
        button.textContent = 'Added to Cart!';
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } catch (error) {
        console.error('Find Your Fate cart:', error);
        button.textContent = 'Try Again';
      } finally {
        button.disabled = false;
      }
    });
    card.appendChild(button);
    return card;
  }

  async function showRecommendations(character) {
    const root = document.querySelector('.fyf');
    if (!root || !character) return;
    const area = root.querySelector('[data-fyf-products]');
    const diceGrid = root.querySelector('[data-fyf-dice-grid]');
    const storageGrid = root.querySelector('[data-fyf-storage-grid]');
    const status = root.querySelector('[data-fyf-status]');
    if (!area || !diceGrid || !storageGrid || !status) return;

    const requestId = ++currentRequest;
    area.hidden = false;
    diceGrid.replaceChildren();
    storageGrid.replaceChildren();
    status.textContent = 'Finding dice worthy of your adventurer...';

    // A persistent link at the end of the recommendation section, updated
    // whenever the player generates a different class.
    let moreDice = root.querySelector('[data-fyf-more-dice]');
    if (!moreDice) {
      moreDice = element('p', 'fyf-more-dice');
      moreDice.setAttribute('data-fyf-more-dice', '');
      moreDice.style.textAlign = 'center';
      moreDice.style.fontSize = '16px';
      moreDice.style.margin = '22px 0 0';
      status.insertAdjacentElement('afterend', moreDice);
    }
    moreDice.replaceChildren();

    const handle = classCollections[character.className];
    if (!handle) {
      status.textContent = 'We could not match that character class.';
      return;
    }

    moreDice.appendChild(document.createTextNode('Looking for more options? '));
    const browseLink = element('a', '',
      'Shop all dice for the ' + character.className + ' class →');
    browseLink.href = rootPath() + 'collections/' + encodeURIComponent(handle);
    browseLink.style.color = 'var(--fyf-accent)';
    browseLink.style.fontWeight = '800';
    browseLink.style.textDecoration = 'underline';
    moreDice.appendChild(browseLink);

    try {
      // Color feed failure should not break otherwise working product recommendations.
      const [diceProducts, storageProducts, colorResult] = await Promise.all([
        fetchCollection(handle),
        fetchCollection('dice-storage'),
        fetchColors(handle).then(data => ({ data, failed: false }))
          .catch(error => {
            console.warn('Find Your Fate color feed:', error);
            return { data: null, failed: true };
          })
      ]);
      if (requestId !== currentRequest) return;

      const dice = chooseDice(diceProducts, character, colorResult.data);
      const accessories = chooseAccessories(storageProducts);
      dice.forEach(item => {
        const card = productCard(item.product, item.label, true);
        if (card) diceGrid.appendChild(card);
      });
      accessories.forEach(item => {
        const card = productCard(item.product, item.label);
        if (card) storageGrid.appendChild(card);
      });

      if (!dice.length) {
        status.textContent = 'No dice recommendations are currently available.';
      } else if (colorResult.failed) {
        status.textContent = 'Showing class-based recommendations. Color matching is temporarily unavailable.';
      } else if (dice.length < 3 || accessories.length < 2) {
        status.textContent = 'Showing the currently available recommendations.';
      } else {
        status.textContent = '';
      }
    } catch (error) {
      if (requestId !== currentRequest) return;
      console.error('Find Your Fate recommendations:', error);
      status.textContent = 'We could not load recommendations right now. Please try again later.';
    }
  }

  document.addEventListener('fyf:character-generated', event => {
    if (event.detail && event.detail.character) {
      showRecommendations(event.detail.character);
    }
  });

  window.FindYourFateProducts = { showRecommendations };
})();
